// client/src/components/pages/Settings/LexiStarAssets/OwnershipTransferDialog.tsx
//
// Record a change of owning organisation for an asset (D26, T1-S08). The server
// closes the current ownership period and opens the new one at the same
// instant (lexistar.transfer_asset_ownership, migration 019).
//
// Effective from: optional single date (react-datepicker, the single-date picker
// the WeighbridgeCompliance modals use), sent as toApiDateString() 'YYYY-MM-DD'
// = midnight SAST. Empty or today = now. The server does not accept future
// dates yet, nor a date before the current owner's period started.
//
// The organisation list comes from the existing tenants API, which only global
// admins may read; anyone else sees a notice instead of a picker.
//
// ST-00289 (founder decision 2026-11-04): a truck's history stays with the organisation that
// owned it at the time — after the transfer the new owner sees none of the trips, positions or
// figures from before it, and the current owner keeps them. Before confirming, "Download this
// truck's history" asks the data export (M4, server/modules/lexistar/exports) for this one
// truck over the current owner's ownership of it (POST /lexistar/exports {asset_id}), follows
// the job and downloads the ZIP when it is ready. The file also stays under Settings → Data
// export for 7 days. Needs lexistar.export.manage. Converted from .jsx in the same change
// (CLAUDE.md TypeScript rule).
//
// ST-00289 follow-ups (founder decisions 2026-11-05): once the new owner (and date) are chosen,
// the dialog asks GET /asset-ownership/transfer-preview what confirming does and shows it:
//   * the current owner's trackers set to the truck — they move to the new owner on confirm;
//   * its planned trips of the truck — cancelled on confirm ("Truck transferred to <new owner>");
//     their ids go back with the transfer, so nothing is cancelled that was not shown here;
//   * its trips under way — not cancelled, they stay with it;
//   * a backdated date that gives the new owner trips — a warning, and Transfer stays disabled
//     until "I understand" is ticked (the server refuses it otherwise, 409 backdated_trips).
// A 409 (the trips changed meanwhile, or the warning was not acknowledged) shows the server's
// message and reloads the preview.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import '../../OperationOverview/datepicker-custom.css';
import { FaTimes, FaExclamationTriangle, FaExchangeAlt, FaDownload } from 'react-icons/fa';
import ThemedSelect, { type ThemedSelectChangeEvent } from '../../../common/ThemedSelect';
import { useAuth } from '../../../../context/AuthContext';
import {
  toApiDateString,
  formatAppDate,
  formatMiningDateTime,
  MINING_TZ,
} from '../../../../utils/dateUtils';
import {
  listTransferTenants,
  getOwnershipHistory,
  getTransferPreview,
  transferOwnership,
} from '../../../../services/lexistarAssets';
import {
  absoluteUrl,
  downloadLink,
  errorText,
  getExport,
  requestTruckHistory,
  startDownload,
  type ExportJob,
} from '../LexiStarDataExport/exportApi';
import type {
  AssetOwnershipPeriod,
  LexistarAssetSummary,
  OwnershipTransferResult,
  TransferConflictCode,
  TransferPreview,
  TransferTenant,
  TransferTrip,
} from '../../../../types/lexistar-assets';

export interface OwnershipTransferDialogProps {
  isOpen: boolean;
  asset: LexistarAssetSummary | null;
  onClose: () => void;
  onTransferred?: (result: OwnershipTransferResult) => void;
}

const inputClasses =
  'w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm';

const startOfDay = (d: Date): Date => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

/** The export permission the download needs (server/modules/lexistar/exports/common MANAGE). */
export const EXPORT_PERMISSION = 'lexistar.export.manage';
/** How often the download follows its export while it is prepared. */
export const HISTORY_POLL_MS = 3000;
/** How long the dialog follows it before it leaves the rest to Settings → Data export. */
export const HISTORY_WAIT_MS = 10 * 60 * 1000;
/** How many trips of a list are shown before "and N more". */
export const TRIPS_SHOWN = 8;

/** The 409 codes the dialog answers by reloading the preview. */
const CONFLICT_CODES: readonly TransferConflictCode[] = [
  'backdated_trips',
  'planned_trips_changed',
];

/** Trip stages in plain words (the stages a trip under way can be in). */
const STAGE_WORDS: Record<string, string> = {
  PLANNED: 'planned',
  DISPATCHED: 'dispatched',
  ACKNOWLEDGED: 'accepted by the driver',
  EN_ROUTE_TO_SOURCE: 'on the way to load',
  AT_SOURCE: 'at the loading site',
  IN_TRANSIT_LOADED: 'loaded, on the way',
  AT_DESTINATION: 'at the destination',
  IN_TRANSIT_EMPTY: 'returning empty',
};
const stageWords = (stage: string): string => STAGE_WORDS[stage] || stage.toLowerCase();

/** A day in words in South African time, e.g. "1 October 2026". */
const dayInWords = (iso: string | null | undefined): string =>
  formatAppDate(iso, { timeZone: MINING_TZ, day: 'numeric', month: 'long', year: 'numeric' });

const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);

type HistoryState =
  | { phase: 'idle' }
  | { phase: 'preparing'; job: ExportJob | null; notes: string[] }
  | { phase: 'done'; filename: string; notes: string[] }
  | { phase: 'waiting'; notes: string[] }
  | { phase: 'error'; message: string };

type PreviewState =
  | { phase: 'none' }
  | { phase: 'loading' }
  | { phase: 'ready'; preview: TransferPreview }
  | { phase: 'error'; message: string };

const errorMessage = (err: unknown, fallback: string): string => {
  const e = err as { response?: unknown; message?: unknown } | null;
  if (e?.response) return errorText(err, fallback);
  return typeof e?.message === 'string' && e.message ? e.message : fallback;
};

/** The 409 code of a refused transfer, when the dialog should reload what it shows. */
const conflictCodeOf = (err: unknown): TransferConflictCode | null => {
  const res = (err as { response?: { status?: number; data?: { code?: unknown } } } | null)
    ?.response;
  const code = res?.data?.code;
  return res?.status === 409 && CONFLICT_CODES.includes(code as TransferConflictCode)
    ? (code as TransferConflictCode)
    : null;
};

/**
 * ST-00289: request the truck's history export, follow it, download the ZIP when it is ready.
 * Stops following when the dialog closes or the asset changes.
 */
function useTruckHistoryDownload(asset: LexistarAssetSummary | null, isOpen: boolean) {
  const [state, setState] = useState<HistoryState>({ phase: 'idle' });
  const run = useRef(0);

  useEffect(() => {
    run.current += 1; // a new asset or a closed dialog stops any follow-up
    setState({ phase: 'idle' });
  }, [asset, isOpen]);
  useEffect(
    () => () => {
      run.current += 1;
    },
    []
  );

  const start = useCallback(async () => {
    if (!asset) return;
    run.current += 1;
    const mine = run.current;
    const alive = () => run.current === mine;
    const tenantId = asset.tenant_id ?? null;
    setState({ phase: 'preparing', job: null, notes: [] });
    try {
      const { job, notes } = await requestTruckHistory({ assetId: asset.id, tenantId });
      if (!alive()) return;
      setState({ phase: 'preparing', job, notes });
      const until = Date.now() + HISTORY_WAIT_MS;
      let current = job;
      while (alive() && (current.state === 'queued' || current.state === 'running')) {
        if (Date.now() > until) {
          setState({ phase: 'waiting', notes });
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, HISTORY_POLL_MS));
        if (!alive()) return;
        current = await getExport(job.id, tenantId);
        if (!alive()) return;
        setState({ phase: 'preparing', job: current, notes });
      }
      if (!alive()) return;
      if (current.state !== 'done') {
        setState({
          phase: 'error',
          message:
            current.error || 'The export did not finish. Try again, or use Settings → Data export.',
        });
        return;
      }
      const link = await downloadLink(job.id, tenantId);
      if (!alive()) return;
      startDownload(absoluteUrl(link.url), link.filename);
      setState({ phase: 'done', filename: link.filename, notes });
    } catch (err) {
      if (!alive()) return;
      setState({
        phase: 'error',
        message: errorMessage(err, "Could not prepare this truck's history."),
      });
    }
  }, [asset]);

  return { state, start };
}

/** A short list of trips: reference and when (planned start, else dispatch), "and N more". */
function TripList({
  trips,
  detail,
}: {
  trips: TransferTrip[];
  detail: (t: TransferTrip) => string;
}) {
  const shown = trips.slice(0, TRIPS_SHOWN);
  return (
    <ul className="mt-1 space-y-0.5 text-xs text-slate-700">
      {shown.map((t) => (
        <li key={t.id}>
          <span className="font-mono font-medium text-slate-900">{t.reference || 'Trip'}</span>
          <span className="text-slate-500"> · {detail(t)}</span>
        </li>
      ))}
      {trips.length > shown.length && (
        <li className="text-slate-500">and {trips.length - shown.length} more</li>
      )}
    </ul>
  );
}

const OwnershipTransferDialog: React.FC<OwnershipTransferDialogProps> = ({
  isOpen,
  asset,
  onClose,
  onTransferred,
}) => {
  const { hasPermission } = useAuth();
  const [tenants, setTenants] = useState<TransferTenant[]>([]);
  const [tenantsError, setTenantsError] = useState<string | null>(null);
  const [current, setCurrent] = useState<AssetOwnershipPeriod | null>(null);
  const [toTenantId, setToTenantId] = useState('');
  const [effectiveDate, setEffectiveDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewState, setPreviewState] = useState<PreviewState>({ phase: 'none' });
  const [acknowledged, setAcknowledged] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const history = useTruckHistoryDownload(asset, isOpen);

  // Today (or nothing) = now: midnight could predate an ownership that started today.
  const effectiveFrom =
    effectiveDate && startOfDay(effectiveDate).getTime() < startOfDay(new Date()).getTime()
      ? toApiDateString(effectiveDate)
      : undefined;

  useEffect(() => {
    if (!isOpen || !asset) return undefined;
    let alive = true;
    setToTenantId('');
    setEffectiveDate(null);
    setError(null);
    setTenantsError(null);
    setCurrent(null);
    setLoading(true);

    Promise.allSettled([listTransferTenants(), getOwnershipHistory(asset.id)]).then(
      ([tenantsRes, historyRes]) => {
        if (!alive) return;
        if (tenantsRes.status === 'fulfilled') {
          setTenants(tenantsRes.value || []);
        } else {
          setTenants([]);
          const status = (tenantsRes.reason as { response?: { status?: number } } | null)?.response
            ?.status;
          setTenantsError(
            status === 403
              ? 'Only a global admin can see the list of organisations. Ask one to record this transfer.'
              : 'Could not load organisations.'
          );
        }
        if (historyRes.status === 'fulfilled') {
          const rows = historyRes.value?.ownership || [];
          setCurrent(rows.find((r) => r.is_current) || null);
        }
        setLoading(false);
      }
    );
    return () => {
      alive = false;
    };
  }, [isOpen, asset]);

  // What confirming does, for the chosen owner and date (ST-00289 follow-ups).
  useEffect(() => {
    setAcknowledged(false);
    if (!isOpen || !asset || !toTenantId) {
      setPreviewState({ phase: 'none' });
      return undefined;
    }
    let alive = true;
    setPreviewState({ phase: 'loading' });
    getTransferPreview({
      assetId: asset.id,
      toTenantId: parseInt(toTenantId, 10),
      effectiveFrom,
    }).then(
      (preview) => {
        if (alive) setPreviewState({ phase: 'ready', preview });
      },
      (err: unknown) => {
        if (alive) {
          setPreviewState({
            phase: 'error',
            message: errorMessage(err, "Could not check this truck's tracker and trips."),
          });
        }
      }
    );
    return () => {
      alive = false;
    };
  }, [isOpen, asset, toTenantId, effectiveFrom, previewKey]);

  if (!isOpen || !asset) return null;

  const options = tenants.filter((t) => t.tenant_id !== asset.tenant_id);
  const target = options.find((t) => String(t.tenant_id) === String(toTenantId));
  const minDate = current?.effective_from ? new Date(current.effective_from) : undefined;
  const owner = asset.tenant_name || `organisation ${asset.tenant_id}`;
  const canExport = hasPermission(EXPORT_PERMISSION);
  const hs = history.state;
  const preparing = hs.phase === 'preparing';
  const preview = previewState.phase === 'ready' ? previewState.preview : null;
  const toName = target?.tenant_name || preview?.to.tenant_name || 'the new owner';
  const fromName = preview?.from.tenant_name || owner;
  const backdated = preview?.backdated || null;
  const needsAck = !!backdated?.requires_acknowledgement;
  const previewBlocks =
    !!toTenantId && (previewState.phase !== 'ready' || (needsAck && !acknowledged));
  const transferDisabled = saving || !!tenantsError || loading || previewBlocks;

  const handleSubmit = async () => {
    if (!toTenantId) {
      setError('Choose the organisation that now owns this asset');
      return;
    }
    if (!preview) return;
    setSaving(true);
    setError(null);
    try {
      const result = await transferOwnership({
        assetId: asset.id,
        toTenantId: parseInt(toTenantId, 10),
        effectiveFrom,
        acknowledgeBackdatedTrips: needsAck ? acknowledged : undefined,
        plannedTripIds: preview.planned_trips.map((t) => t.id),
      });
      onTransferred?.(result);
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Transfer failed'));
      // The trips changed meanwhile, or the warning was not confirmed: show what is true now.
      if (conflictCodeOf(err)) setPreviewKey((k) => k + 1);
    } finally {
      setSaving(false);
    }
  };

  const progressText = (job: ExportJob | null): string => {
    if (!job || job.state === 'queued') return 'Queued: the file is prepared in the background…';
    const rows = job.progress?.rows;
    return rows
      ? `Preparing the file: ${rows.toLocaleString('en-ZA')} rows so far…`
      : 'Preparing the file…';
  };

  const trackers = preview?.trackers || [];
  const planned = preview?.planned_trips || [];
  const underway = preview?.underway_trips || [];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm">
      {/* A tall dialog scrolls with the page; the dialog itself stays overflow-visible so the date picker is not clipped. */}
      <div className="min-h-full flex items-center justify-center py-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Transfer ${asset.registration}`}
          className="bg-surface rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-visible"
          style={{ animation: 'fadeInScale 0.2s ease-out' }}
        >
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-900">Transfer ownership</h3>
            <button
              onClick={onClose}
              aria-label="Close"
              className="text-slate-400 hover:text-slate-600 transition-colors"
            >
              <FaTimes size={18} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            {error && (
              <div role="alert" className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Asset</label>
              <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900">
                <span className="font-mono font-medium">{asset.registration}</span>
                <span className="text-slate-500">
                  {' '}
                  — owned by {owner}
                  {current?.effective_from ? ` since ${formatAppDate(current.effective_from)}` : ''}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">New owner</label>
              {tenantsError ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <FaExclamationTriangle
                    className="text-amber-500 mt-0.5 flex-shrink-0"
                    size={14}
                  />
                  <p className="text-sm text-amber-800">{tenantsError}</p>
                </div>
              ) : (
                <ThemedSelect
                  value={toTenantId}
                  onChange={(e: ThemedSelectChangeEvent) =>
                    setToTenantId(String(e.target.value ?? ''))
                  }
                  className={inputClasses}
                  disabled={loading}
                  aria-label="New owner"
                >
                  <option value="">{loading ? 'Loading organisations...' : 'Choose...'}</option>
                  {options.map((t) => (
                    <option key={t.tenant_id} value={t.tenant_id}>
                      {t.tenant_name} ({t.tenant_code})
                    </option>
                  ))}
                </ThemedSelect>
              )}
            </div>

            <div>
              <label
                htmlFor="lexistar-transfer-effective-from"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Effective from
              </label>
              <DatePicker
                id="lexistar-transfer-effective-from"
                selected={effectiveDate}
                onChange={(d: Date | null) => setEffectiveDate(d)}
                maxDate={new Date()}
                minDate={minDate}
                dateFormat="yyyy/MM/dd"
                placeholderText="Now"
                isClearable
                className={inputClasses}
                wrapperClassName="w-full"
              />
              <p className="mt-1 text-xs text-slate-500">
                Leave empty, or pick today, to transfer now. An earlier date records when the sale
                happened.
              </p>
            </div>

            {toTenantId && (
              <section
                aria-labelledby="lexistar-transfer-effects-title"
                className="p-3 border border-slate-200 rounded-lg space-y-3"
              >
                <h4
                  id="lexistar-transfer-effects-title"
                  className="text-sm font-semibold text-slate-800"
                >
                  What the transfer does to {asset.registration}&apos;s tracker and trips
                </h4>
                {previewState.phase === 'loading' && (
                  <p role="status" className="text-xs text-slate-600">
                    Checking the truck&apos;s tracker and trips…
                  </p>
                )}
                {previewState.phase === 'error' && (
                  <div className="text-xs space-y-1">
                    <p className="text-red-600">{previewState.message}</p>
                    <button
                      type="button"
                      onClick={() => setPreviewKey((k) => k + 1)}
                      className="text-primary-600 hover:underline"
                    >
                      Try again
                    </button>
                  </div>
                )}
                {preview && (
                  <>
                    <div role="group" aria-label="Trackers">
                      {trackers.length > 0 ? (
                        <>
                          <p className="text-xs font-medium text-slate-800">
                            {plural(trackers.length, 'Tracker', 'Trackers')} that{' '}
                            {plural(trackers.length, 'moves', 'move')} to {toName} (
                            {trackers.length})
                          </p>
                          <ul className="mt-1 space-y-0.5 text-xs text-slate-700">
                            {trackers.map((d) => (
                              <li key={d.device_id}>
                                <span className="font-mono font-medium text-slate-900">
                                  IMEI {d.imei}
                                </span>
                                <span className="text-slate-500">
                                  {[d.label, d.model].filter(Boolean).length
                                    ? ` · ${[d.label, d.model].filter(Boolean).join(' · ')}`
                                    : ''}
                                  {d.status !== 'active' ? ' · switched off' : ''}
                                </span>
                              </li>
                            ))}
                          </ul>
                          <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                            {plural(trackers.length, 'It moves', 'They move')} when you confirm:
                            from then on {plural(trackers.length, 'its', 'their')} positions go to{' '}
                            {toName}. Positions already received stay with {fromName}.
                          </p>
                        </>
                      ) : (
                        <p className="text-xs text-slate-600">
                          {fromName} has no tracker set to {asset.registration}, so no tracker
                          moves.
                        </p>
                      )}
                    </div>

                    <div role="group" aria-label="Planned trips">
                      {planned.length > 0 ? (
                        <>
                          <p className="text-xs font-medium text-slate-800">
                            Planned {plural(planned.length, 'trip', 'trips')} that will be cancelled
                            ({planned.length})
                          </p>
                          <TripList
                            trips={planned}
                            detail={(t) =>
                              t.planned_start
                                ? `planned for ${formatMiningDateTime(t.planned_start)}`
                                : 'planned'
                            }
                          />
                          <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                            {fromName} planned {plural(planned.length, 'it', 'them')} but cannot run{' '}
                            {plural(planned.length, 'it', 'them')} once the truck is {toName}
                            &apos;s. {plural(planned.length, 'It is', 'They are')} cancelled with
                            the reason &ldquo;{preview.cancellation_reason}&rdquo;.
                          </p>
                        </>
                      ) : (
                        <p className="text-xs text-slate-600">
                          {fromName} has no planned trips of {asset.registration} to cancel.
                        </p>
                      )}
                    </div>

                    {underway.length > 0 && (
                      <div role="group" aria-label="Trips under way">
                        <p className="text-xs font-medium text-slate-800">
                          {plural(underway.length, 'Trip', 'Trips')} under way:{' '}
                          {plural(underway.length, 'it stays', 'they stay')} with {fromName}{' '}
                          {`(${underway.length})`}
                        </p>
                        <TripList trips={underway} detail={(t) => stageWords(t.stage)} />
                        <p className="mt-1 text-xs text-slate-600 leading-relaxed">
                          {plural(underway.length, 'It is', 'They are')} not cancelled, and{' '}
                          {fromName} keeps {plural(underway.length, 'it', 'them')}.{' '}
                          {plural(underway.length, 'Its track stops', 'Their tracks stop')} at the
                          transfer. Close {plural(underway.length, 'it', 'them')} on the trip page
                          when {plural(underway.length, 'it is', 'they are')} done.
                        </p>
                      </div>
                    )}
                  </>
                )}
              </section>
            )}

            {backdated && needsAck && (
              <section
                aria-labelledby="lexistar-transfer-backdated-title"
                className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2"
              >
                <h4
                  id="lexistar-transfer-backdated-title"
                  className="text-sm font-semibold text-amber-900 flex items-start gap-2"
                >
                  <FaExclamationTriangle
                    className="text-amber-500 mt-0.5 flex-shrink-0"
                    size={14}
                  />
                  <span>
                    {backdated.trips} {plural(backdated.trips, 'trip', 'trips')} since{' '}
                    {dayInWords(backdated.since)} will move to {toName}
                  </span>
                </h4>
                <p className="text-xs text-amber-800 leading-relaxed">
                  From {dayInWords(backdated.since)} the truck counts as {toName}&apos;s. The trips
                  started on it since then count as {toName}&apos;s trips on its truck, with their
                  figures, including the ones {fromName} ran before this transfer was recorded.{' '}
                  {fromName} keeps the trips it planned.
                  {backdated.planned > 0
                    ? ` ${backdated.planned} of them ${plural(backdated.planned, 'is a planned trip', 'are planned trips')} this transfer cancels.`
                    : ''}
                </p>
                <p className="text-xs text-amber-800 leading-relaxed">
                  The tracker moves now, not on {dayInWords(backdated.since)}: the truck&apos;s
                  positions up to now stay with {fromName}, and Replay of the truck shows {toName}{' '}
                  none of them.
                </p>
                <label className="flex items-start gap-2 text-xs font-medium text-amber-900">
                  <input
                    type="checkbox"
                    checked={acknowledged}
                    onChange={(e) => setAcknowledged(e.target.checked)}
                    className="mt-0.5"
                  />
                  <span>
                    I understand: {backdated.trips} {plural(backdated.trips, 'trip', 'trips')} since{' '}
                    {dayInWords(backdated.since)} move to {toName}.
                  </span>
                </label>
              </section>
            )}
            {backdated && !needsAck && (
              <p className="text-xs text-slate-600">
                Backdated to {dayInWords(backdated.since)}: no trip was started on{' '}
                {asset.registration} since then, so no trip moves to {toName}.
              </p>
            )}

            <section
              aria-labelledby="lexistar-transfer-history-title"
              className="p-3 border border-slate-200 rounded-lg space-y-2"
            >
              <h4
                id="lexistar-transfer-history-title"
                className="text-sm font-semibold text-slate-800"
              >
                This truck&apos;s history stays with {owner}
              </h4>
              <p className="text-xs text-slate-600 leading-relaxed">
                After the transfer, {target ? target.tenant_name : 'the new owner'} sees none of{' '}
                {asset.registration}&apos;s trips, positions or figures from before it, and {owner}{' '}
                keeps them in LexiStar. Download a copy now for your records.
              </p>
              {canExport ? (
                <button
                  type="button"
                  onClick={() => void history.start()}
                  disabled={preparing}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all ${
                    preparing
                      ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                      : 'border-primary-500 text-primary-600 hover:bg-primary-50'
                  }`}
                >
                  <FaDownload size={12} />
                  {preparing ? 'Preparing…' : "Download this truck's history"}
                </button>
              ) : (
                <p className="text-xs text-slate-500">
                  Downloading it needs the Export LexiStar Data permission.
                </p>
              )}
              <div role="status" aria-live="polite" className="text-xs">
                {hs.phase === 'preparing' && (
                  <p className="text-slate-600">{progressText(hs.job)}</p>
                )}
                {hs.phase === 'done' && (
                  <p className="text-emerald-700">
                    Downloaded {hs.filename}. It also stays under Settings → Data export for 7 days.
                  </p>
                )}
                {hs.phase === 'waiting' && (
                  <p className="text-slate-600">
                    Still being prepared. It carries on in the background: download it from Settings
                    → Data export when it is ready.
                  </p>
                )}
                {hs.phase === 'error' && <p className="text-red-600">{hs.message}</p>}
                {(hs.phase === 'preparing' || hs.phase === 'done' || hs.phase === 'waiting') &&
                  hs.notes.map((n) => (
                    <p key={n} className="text-slate-500">
                      {n}
                    </p>
                  ))}
              </div>
            </section>

            {target && (
              <p className="text-sm text-slate-600 leading-relaxed">
                <span className="font-mono font-medium text-slate-900">{asset.registration}</span>{' '}
                will belong to <strong>{target.tenant_name}</strong> from{' '}
                {effectiveFrom ? formatAppDate(effectiveDate) : 'now'}. After that only{' '}
                {target.tenant_name} and global admins see it.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 rounded-b-2xl">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={transferDisabled}
              className={`inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-lg transition-all shadow-sm hover:shadow ${transferDisabled ? 'bg-slate-400 cursor-not-allowed' : 'bg-primary-500 hover:bg-primary-600'}`}
            >
              <FaExchangeAlt size={12} />
              {saving ? 'Transferring...' : 'Transfer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnershipTransferDialog;
